import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { useEffect } from 'react';
import { overlayActions } from 'store/slices/overlay';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'hooks/useReduxHooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UpdateTypeLiterals, UserCredentials } from 'interfaces/deployment';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import DeploymentService from 'services/DeploymentService';
import { ReactQueryKeys } from 'constants/react-query-keys';
import {
  FRONT_ROUTER,
  DEPLOYMENT_UPDATE_PARTIAL_PARAMS_KEY,
} from 'constants/router';
import { PARTIAL_UPDATE_TYPE } from 'constants/deployment';
import Button from 'components/UI/Button/Button';
import { selectUser } from 'store/slices/auth';
import CredentialsFields from '../CredentialsFields/CredentialsFields';

/**
 * Type definition for URL parameters containing the update type
 */
type ParamsWithUpdateType = {
  [DEPLOYMENT_UPDATE_PARTIAL_PARAMS_KEY]: UpdateTypeLiterals;
};

/**
 * @description Component for updating partial deployments with credential authentication (revert, update to full library, etc.).
 *
 * @SADtrace SAD-003-6.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @returns {JSX.Element} Rendered component
 */
const UpdatePartialDeploymentModal = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);

  const initialState: UserCredentials = {
    password: '',
    username: user?.email || '',
  };

  const formMethods = useForm<UserCredentials>({
    defaultValues: initialState,
  });
  const { handleSubmit, reset } = formMethods;

  const location = useLocation();
  const backUrl =
    (location.state as string) ?? FRONT_ROUTER.DEPLOYMENT_FULL_DEPLOYMENT_TAB;

  const navigate = useNavigate();

  const { [DEPLOYMENT_UPDATE_PARTIAL_PARAMS_KEY]: updateType } =
    useParams<ParamsWithUpdateType>();

  const queryClient = useQueryClient();

  /**
   * @description Mutation hook for updating partial deployment
   * Handles success and error cases, including toast notifications
   */
  const { isPending, mutate } = useMutation({
    mutationFn: (data: UserCredentials) =>
      DeploymentService.updatePartialDeployment(
        data,
        // v8 ignore start -- WAIVER(MICRO): every opener passes an explicit updateType, so the fallback arm exists only to satisfy the optional prop type
        updateType ?? 'update-to-full-library',
        // v8 ignore stop -- WAIVER(MICRO)
      ),

    onSuccess: () => {
      toast.success(t('deployment.messages.deployment__success'));
      reset();
      queryClient.invalidateQueries({
        queryKey: [ReactQueryKeys.DEPLOYMENTS],
      });
      queryClient.invalidateQueries({
        queryKey: [ReactQueryKeys.PARTIAL_DEPLOYMENT],
      });
      queryClient.invalidateQueries({
        queryKey: [ReactQueryKeys.CAN_DEPLOY],
      });
      navigate(backUrl);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || t('errors.default'));
    },
  });

  /**
   * @description Open the overlay when component mounts
   */
  useEffect(() => {
    dispatch(overlayActions.open({ path: backUrl }));
  }, [dispatch, backUrl]);

  /**
   * @description Form submission handler
   * Triggers the update partial deployment mutation
   *
   * @param {UserCredentials} data - The form data containing user credentials
   */
  const handleFormSubmit: SubmitHandler<UserCredentials> = (data) => {
    mutate(data);
  };

  // Redirect to error page if update type is invalid
  if (!updateType || !Object.values(PARTIAL_UPDATE_TYPE).includes(updateType)) {
    return <Navigate to={FRONT_ROUTER.ERROR} />;
  }

  return (
    <form
      id="formid"
      autoComplete="off"
      className="form-fixed-width"
      onSubmit={handleSubmit(handleFormSubmit)}
    >
      <div className="modal-sidebar-body">
        <CredentialsFields formMethods={formMethods} isModal />
      </div>

      <div className="modal-sidebar-footer">
        <Button
          loading={isPending}
          defaultLabel={
            updateType === PARTIAL_UPDATE_TYPE.REVERT
              ? t('buttons.save')
              : t('deployment.buttons.deploy_btn')
          }
          loadingLabel={t('buttons.updating')}
          type="submit"
          disabled={isPending}
          data-testid="update_partial_deploymet_submit_btn"
        ></Button>
        <Link
          to={backUrl}
          className={`btn btn-lg rounded btn-secondary ${
            isPending ? 'disabled' : ''
          }`}
        >
          {t('buttons.cancel')}
        </Link>
      </div>
    </form>
  );
};

export default UpdatePartialDeploymentModal;
